<template>
	<!-- Dialog component -->
	<div v-show="showDialog"
		:class="{ 'ui-dialog__backdrop': backdrop }"
		class="ui-dialog"
		@click="onBackdropClick($event)"
	>
		<transition name="fade">
			<div
				v-if="showDialog"
				:class="{ 'ui-dialog__large': large }"
				class="ui-dialog__wrapper"
			>
				<div class="ui-dialog__content">
					<ui-button
						v-if="closable"
						class="ui-dialog__close-button"
						dense
						icon="fal fa-times"
						@click="close"
					/>
					<div :style="closable ? { 'margin-right': '32px' } : {}" class="ui-dialog__title">
						<h6 v-if="title" class="ui-title">{{ title }}</h6>
						<slot name="header"/>
					</div>

					<slot/>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import UiButton from "lib/components/ui/Button";

import { getScrollbarWidth } from "lib/modules/Util";

export default {
	name: "Dialog",
	components: {
		UiButton
	},
	props: {
		backdrop: {
			type: Boolean,
			default: true,
			required: false
		},
		closable: {
			type: Boolean,
			default: false,
			required: false
		},
		large: {
			type: Boolean,
			default: false,
			required: false
		},
		title: {
			type: String,
			default: "",
			required: false
		}
	},
	data() {
		return {
			showDialog: false
		};
	},
	methods: {
		open() {
			// add event listeners
			addEventListener("keyup", this.onEscapeKeyUp);
			// setting ui-dialog__open class on body to prevent scrolling body when dialog is open
			document.body.classList.add("ui-dialog__open");
			const scrollbarWidth = getScrollbarWidth();
			// add right padding same size as scrollbar (if present) to prevent UI glitch
			if (scrollbarWidth > 0 && window.innerHeight < document.body.clientHeight)
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			this.showDialog = true;
			// event needs to emit in next tick to make sure dialog fully exist in DOM
			this.$nextTick(() => {
				this.$emit("open");
			});
		},
		close() {
			// remove event listeners
			removeEventListener("keyup", this.onEscapeKeyUp);
			// remove ui-dialog__open class on body to re-enable body scroll
			document.body.classList.remove("ui-dialog__open");
			// remove the padding-right property
			document.body.style.removeProperty("padding-right");
			this.showDialog = false;
			// event needs to emit in next tick to make sure dialog fully removed from DOM
			this.$nextTick(() => {
				this.$emit("close");
			});
		},
		onEscapeKeyUp(evt) {
			if (evt.which === 27 && this.closable) {
				this.close();
			}
		},
		onBackdropClick(evt) {
			if (typeof evt.target.classList !== "undefined" && evt.target.classList.contains("ui-dialog") && this.closable)
				this.close();
		}
	}
};
</script>

<style lang="scss" scoped>
.ui-dialog {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	overflow-x: hidden;
	overflow-y: auto;
	z-index: 8;
	&.ui-dialog__backdrop {
		background-color: rgba(0,0,0,0.6);
	}
	.ui-dialog__wrapper {
		position: relative;
		width: 768px;
		max-width: 80%;
		margin: 0 auto;
		box-shadow:
			0 11px 15px -7px rgba(0, 0, 0, 0.2),
			0 24px 38px 3px rgba(0, 0, 0, 0.14),
			0 9px 46px 8px rgba(0, 0, 0, 0.12);

		&.ui-dialog__large {
			width: 66%;
		}
		.ui-dialog__content {
			position: relative;
			margin: 16px 0;
			padding: 18px;
			border-radius: 2px;
			background-color: #fff;
		}
		.ui-dialog__close-button {
			position: absolute;
			top: 10px;
			right: 4px;
		}
		/deep/ .ui-dialog__title {
			padding: 0;
			position: relative;
			> * {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				margin-top: 0;
				margin-bottom: 20px;
			}
		}
	}
}
@media only screen and (max-width: 576px) {
	.ui-dialog {
		.ui-dialog__wrapper {
			position: fixed;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: auto !important;
			max-width: 100%;
			max-height: 100%;
			.ui-dialog__content {
				height: 100%;
				margin: 0;
			}
		}
	}
}
// transition block
.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.fade-leave,
.fade-enter-to {
	opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
	transition: all 0.3s;
}
</style>
